import React, { useState } from "react";
import { MapPin, School, GraduationCap, MessageCircle } from "lucide-react";
import { ChartActionsButton } from "@/components/ChartActionsButton";
import { useTranslation } from "react-i18next";
import { InterestsBadgeList } from "@/components/InterestsBadgeList";
import { processStudent } from "@/util/studentUtils";
import { BentoSection } from "./BentoSection";
import { StudentFunnelStateSection } from "./StudentFunnelStateSection";
import { ContactSection } from "./ContactSection";
import { FunFactsSection } from "./FunFactsSection";
import { BanFromOrg } from "@/components/BanFromOrg";
import { ReportUser } from "@/components/ReportUser";
import { PredictiveScoreSection } from "./PredictiveScoreSection";
import { StudentTimelineSection } from "./StudentTimelintSection";
import { Bouncer } from "@/components/Bouncer";
import { useShareLink } from "@/hooks/useShareLink";
import { cleanUtf8, standardFormatDateLong } from "@/util/stringUtils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import {
  FafsaCompletionStatusEnum,
  StudentTypeEnum,
  YesNoEnum,
  FunnelStateEnum,
} from "@/__generated__/types";
import { useStudent } from "@/hooks/useStudent";
import { useParams } from "react-router-dom";
import { useOrg } from "@/hooks/useOrg";
import { serializeQualifiedChatId } from "@zeemee/react-web-chat";
import { useProtectedNavigation } from "@/routes/useProtectedNavigation";
import { StudentProfileImage } from "@/components/StudentGrid/StudentProfileImage";
import { StudentHeader } from "@/components/StudentGrid/StudentHeader";

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <li className="flex items-center justify-between">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-semibold text-foreground">{value}</span>
  </li>
);

export const StudentProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const { studentId } = useParams<{ studentId: string }>();
  const { org } = useOrg();
  const [banDialogOpen, setBanDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const { navToRoute } = useProtectedNavigation();

  const {
    student: studentData,
    loading,
    error,
  } = useStudent(studentId || "", org?.id || "");

  const student = studentData ? processStudent(studentData) : {};
  const { shareCurrentLink } = useShareLink();

  if (!studentId || !org?.id) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-lg text-destructive">
          {t("studentCard.notFound")}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Bouncer />
      </div>
    );
  }

  if (error || !studentData || student?.__typename !== "Student") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-lg text-destructive">
          {error
            ? t("global.error", { message: error.message })
            : t("studentCard.notFound")}
        </div>
      </div>
    );
  }

  const studentStatus = student.studentOrganizationStatus;

  return (
    <div
      className="flex min-h-screen flex-col bg-background lg:flex-row"
      role="main"
    >
      {/* Left column: Profile card */}
      <div className="flex w-full items-start border-b border-border bg-transparent lg:max-w-sm lg:border-r lg:border-b-0">
        <div className="w-full border-b border-border" role="article">
          {/* Profile Image with overlays */}
          <div
            className="bg-student-profile-gradient px-6 pt-6"
            style={{
              background: "var(--student-profile-gradient)",
            }}
          >
            <StudentProfileImage
              student={student}
              className="size-84 object-cover"
            />
          </div>
          {/* Name and last active */}
          <div className="px-6 pb-6">
            {/*
            We are hiding this for now but will reinstate in the future.
            <div className="relative mt-4 mb-2">
              <div
                className="absolute top-0 right-0 -translate-y-2 text-xs text-muted-foreground"
                role="status"
              >
                <span>{getLastActiveLabel(student.lastSignInAt || "")}</span>
              </div>
            </div>
            */}
            <div className="mb-4 grid grid-cols-1 items-center gap-4">
              <div className="mt-3">
                <StudentHeader student={student} />
              </div>
            </div>
            {/* Student details */}
            <div
              className="mb-4 flex flex-col gap-1 text-muted-foreground"
              role="list"
            >
              <div className="mb-1 flex items-start gap-2" role="listitem">
                <MapPin
                  className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
                <span className="flex-1">
                  {student.studentOrganizationStatus?.location || (
                    <span className="text-muted-foreground">
                      {t("studentCard.noLocation")}
                    </span>
                  )}
                </span>
              </div>
              <div className="mb-1 flex items-center gap-2" role="listitem">
                <School
                  className="h-4 w-4 text-muted-foreground"
                  aria-hidden="true"
                />
                {student.studentOrganizationStatus?.highSchool || (
                  <span className="text-muted-foreground">
                    {t("studentCard.noHighSchool")}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2" role="listitem">
                <GraduationCap
                  className="h-4 w-4 text-muted-foreground"
                  aria-hidden="true"
                />
                {student.majors?.map(({ name }) => name).join(", ") || (
                  <span className="text-muted-foreground">
                    {t("studentCard.noMajors")}
                  </span>
                )}
              </div>
            </div>
            {/* Bio section */}
            <div className="mb-4 wrap-break-word text-muted-2">
              {cleanUtf8(student.studentOrganizationStatus?.bio) || (
                <span className="text-muted-foreground-2 text-sm">
                  {t("studentCardPopover.noBio")}
                </span>
              )}
            </div>
            {/* Interests section */}
            <InterestsBadgeList
              limit={10000}
              interests={
                student.interests
                  ?.map(({ name }) => name)
                  .filter((name): name is string => Boolean(name)) || []
              }
              colored
            />
            {/* Social media links */}
            <div
              className="my-4 flex gap-4"
              role="list"
              aria-label={t("global.socialMediaLinks")}
            >
              {student.socials
                ?.filter((social) => social.platformName)
                .map((social, index: number) => {
                  const platformName = social.platformName || "";
                  const baseUrl = social.baseUrl;
                  const handle = social.handle;
                  const iconUrl = social.iconUrl;

                  const iconContent = iconUrl ? (
                    <img
                      src={iconUrl}
                      alt={`${platformName} icon`}
                      className="h-8 w-8"
                      style={{
                        filter: "brightness(0)",
                        color: "var(--color-foreground)",
                      }}
                    />
                  ) : (
                    <div className="flex h-6 w-6 items-center justify-center rounded bg-gray-300 text-xs text-gray-600">
                      {platformName.charAt(0).toUpperCase()}
                    </div>
                  );

                  return (
                    <div
                      key={index}
                      className="flex items-center justify-center rounded-full border border-border transition-colors hover:border-gray-400 hover:bg-gray-100"
                      role="listitem"
                    >
                      {baseUrl && handle ? (
                        <a
                          href={`${baseUrl}${handle}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={t("studentCard.socialLink", {
                            name: student.studentOrganizationStatus?.firstName,
                            platform: platformName,
                          })}
                        >
                          {iconContent}
                        </a>
                      ) : (
                        iconContent
                      )}
                    </div>
                  );
                })}
            </div>
            {/* Action buttons */}
            <div className="flex gap-2">
              <button
                className="flex flex-1 items-center justify-center gap-2 rounded-md bg-slate-700 py-2 font-light text-white transition-colors hover:bg-slate-600"
                aria-label={t("studentCardPopover.message")}
                onClick={() =>
                  navToRoute("selectedChat", {
                    chat:
                      serializeQualifiedChatId({
                        chatType: "chat",
                        chatId: student.uuid ?? "",
                      }) || "",
                  })
                }
              >
                <MessageCircle className="h-4 w-4" aria-hidden="true" />
                {t("studentCardPopover.message")}
              </button>

              <div className="flex items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <ChartActionsButton className="rotate-90" />
                  </DropdownMenuTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => shareCurrentLink()}>
                        {t("studentCard.overflowMenu.share")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setBanDialogOpen(true)}>
                        {t("studentCard.overflowMenu.banFromOrg")}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => setReportDialogOpen(true)}
                      >
                        {t("studentCard.overflowMenu.reportUser")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenuPortal>
                </DropdownMenu>
              </div>
            </div>
          </div>
          {/* Details section */}
          <div className="border-t border-border p-6">
            <h2 className="mb-4 text-[16px] leading-tight font-semibold">
              {t("studentCard.details.title")}
            </h2>
            <ul className="space-y-6 text-sm">
              <DetailRow
                label={t("studentCard.details.assignedCounselor")}
                value={
                  studentStatus?.assignedCounselor ? (
                    studentStatus.assignedCounselor.name
                  ) : (
                    <span className="text-muted-foreground">
                      {t("studentCard.details.noAssignedCounselor")}
                    </span>
                  )
                }
              />
              <DetailRow
                label={t("studentCard.details.joined")}
                value={standardFormatDateLong(studentStatus?.joinedOn) || "-"}
              />
              <DetailRow
                label={t("studentCard.details.cohortYear")}
                value={studentStatus?.cohortYear || "-"}
              />
              <DetailRow
                label={t("studentCard.details.studentType")}
                value={
                  studentStatus?.studentType
                    ? studentStatus.studentType === StudentTypeEnum.FirstYear
                      ? t("filters.fieldOptions.FIRST_YEAR")
                      : studentStatus.studentType === StudentTypeEnum.Transfer
                        ? t("filters.fieldOptions.TRANSFER")
                        : studentStatus.studentType
                    : "-"
                }
              />
              <DetailRow
                label={t("studentCard.details.lookingForRoommate")}
                value={
                  studentStatus?.lookingForRoommate
                    ? studentStatus.lookingForRoommate === YesNoEnum.Yes
                      ? t("filters.fieldOptions.YES")
                      : t("filters.fieldOptions.NO")
                    : "-"
                }
              />
              <DetailRow
                label={t("studentCard.details.hasCompletedRoommateQuiz")}
                value={
                  studentStatus?.hasTakenRoommateQuiz
                    ? studentStatus.hasTakenRoommateQuiz === YesNoEnum.Yes
                      ? t("studentCard.details.finishedQuiz")
                      : t("studentCard.details.hasntFinishedQuiz")
                    : "-"
                }
              />
              <DetailRow
                label={t("studentCard.details.schoolsFollowing")}
                value={studentStatus?.schoolsFollowing || "-"}
              />
              <DetailRow
                label={t("studentCard.details.admittedTo")}
                value={studentStatus?.schoolsAdmittedTo || "-"}
              />
              <DetailRow
                label={t("studentCard.details.eventsAttended")}
                value={studentStatus?.liveEventsAttended || 0}
              />
            </ul>
          </div>
          {/* CRM Data section */}
          <div className="border-t border-border p-6">
            <h2 className="mb-4 text-[16px] leading-tight font-semibold">
              {t("studentCard.crmData.title")}
            </h2>
            <ul className="space-y-6 text-sm">
              <DetailRow
                label={t("studentCard.crmData.crmId")}
                value={studentStatus?.crmId ?? "-"}
              />
              <DetailRow
                label={t("studentCard.crmData.completedFafsa")}
                value={
                  studentStatus?.fafsaCompletionStatus
                    ? studentStatus.fafsaCompletionStatus ===
                      FafsaCompletionStatusEnum.Yes
                      ? t("filters.fieldOptions.YES")
                      : studentStatus.fafsaCompletionStatus ===
                          FafsaCompletionStatusEnum.No
                        ? t("filters.fieldOptions.NO")
                        : t("filters.fieldOptions.NON_FILER")
                    : "-"
                }
              />
              <DetailRow
                label={t("studentCard.crmData.visitedCampus")}
                value={
                  studentStatus?.visitedCampus
                    ? studentStatus.visitedCampus === YesNoEnum.Yes
                      ? t("filters.fieldOptions.YES")
                      : t("filters.fieldOptions.NO")
                    : "-"
                }
              />
            </ul>
          </div>
          {/* Contact section */}
          <ContactSection
            email={studentStatus?.email}
            phoneNumber={studentStatus?.phoneNumber}
          />
        </div>
      </div>
      {/* Right column: Bento row, funnel state, and line chart */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-6 p-6">
        <BentoSection student={studentData} />

        <div className="mt-2">
          <StudentFunnelStateSection
            funnelState={studentStatus?.funnelState || FunnelStateEnum.Inquired}
            funnelStateTransitionSummary={
              studentData?.studentOrganizationStatus
                ?.funnelStateTransitionSummary || null
            }
          />
        </div>

        {/* Predictive Score Section */}
        <div className="mt-2">
          <PredictiveScoreSection orgId={org?.id} student={student} />
        </div>

        {/* Fun Facts Section */}
        <div className="mt-2">
          <FunFactsSection studentId={studentId} />
        </div>

        {/* Student Timeline Section */}
        <div className="mt-2">
          <StudentTimelineSection studentId={studentId} />
        </div>
      </div>

      {/* Ban from organization dialog */}
      <BanFromOrg
        open={banDialogOpen}
        onOpenChange={setBanDialogOpen}
        orgId={org?.id}
        userId={studentId}
      />

      {/* Report user dialog */}
      <ReportUser
        open={reportDialogOpen}
        onOpenChange={setReportDialogOpen}
        userId={studentId}
      />
    </div>
  );
};
